import type { VendorMonkeyProfile } from "./monkey-vendor-scene"

/** WDallgraphics source clips; the rig and animation tracks are kept intact. */
export const capuchinProfile: VendorMonkeyProfile = {
  url: `${import.meta.env.BASE_URL}mascot/wdallgraphics-capuchin.glb`,
  orientation: [0, 0, 0, 1],
  metersPerUnit: 1,
  frameAtSeconds: 0,
  // Highest skinned vertex over all six motions, including the original full jump,
  // plus the idle floor offset: 2.01427245 + 0.0098344 m, rounded up.
  motionHeightMeters: 2.03,
  // All skinned vertices at 120 Hz over entry/jump/exit: max(maxY-minY)
  // .947960 m and max(hypot(width, depth)) 1.098770 m, rounded outward.
  jumpPoseSizeMeters: { width: 1.101, height: 0.95 },
  // Original IdleToSit + JumpInplace + SittingToIdle, all skinned vertices at
  // 120 Hz and both endpoints, relative to the centered idle origin; rounded outward.
  jumpBoundsMeters: {
    min: [-0.467, -0.038, -0.487],
    max: [0.47, 2.026, 0.475]
  },
  // No standUpTurns: the original TurnLeft and TurnRight turn the body a quarter while
  // the feet stay put (3 cm, never lifted) and the hands slide half a metre over the
  // floor; on screen that is a figure on a turntable. He gets up straight and steps round.
  // Root motion of the counterparts in Animations/ (30 fps), measured on the pelvis
  // against the In-place cycles: WalkLeft turns 40 degrees in its 25 frames on a
  // 0.562 m chord, RunLeft 42 degrees in 15 frames on a 1.101 m chord; Right mirrors
  // both. The in-place cycles keep that turn in the pelvis and loop back from it.
  turnGaits: {
    walk: {
      left: "Anim_Monkey_WalkLeft",
      right: "Anim_Monkey_WalkRight",
      degreesPerCycle: 40,
      radiusMeters: 0.821
    },
    run: {
      left: "Anim_Monkey_RunLeft",
      right: "Anim_Monkey_RunRight",
      degreesPerCycle: 42,
      radiusMeters: 1.537
    }
  },
  pelvisBone: "monkey_Pelvis_bone",
  // Its root-motion counterpart steps back 0.5 m in 24 frames at 30 fps; the 25th
  // keyframe repeats the 24th, and the looped cycle leaves it out.
  backStep: { clip: "Anim_Monkey_Backward", speedMetersPerSecond: 0.625 },
  // The original Smile pulls the mouth corners back over 0.3 s, holds 1.6 s and lets go
  // by 2.4 s; at 90 px the face is about 15 px and the grin does not show. White-faced
  // capuchins greet with a silent bared-teeth display and lip-smacking (De Marco et al.
  // 2008): the lips go further off the teeth and the jaw smacks three times in the hold.
  // The original cocks the head far to one side throughout; he keeps it nearly upright
  // and faces you. At 2.4 the lower lip folded over its teeth. The jaw opens the way Eat
  // opens it widest.
  expressions: {
    smile: {
      exaggerate: [
        { tracks: "^monkey_[lr]_(UpperLip|LowerLip)_hlp\\.position$", factor: 1.7 },
        { tracks: "^monkey_[lr]_UpperLip_hlp\\.quaternion$", factor: 1.3 },
        { tracks: "^monkey_[lr]_Lip_hlp\\.position$", factor: 1.5 },
        { tracks: "^monkey_(Neck|Head)_bone\\.quaternion$", factor: 0.3 }
      ],
      lipSmack: {
        openingClip: "Anim_Monkey_Eat",
        jaws: [
          { bone: "monkey_Mandible_bone", degrees: 9 },
          { bone: "monkey_Jaw_bone", degrees: 5 }
        ],
        start: 1,
        end: 1.6,
        hertz: 5
      }
    },
    // A nut is held in the right hand; the left arm rests as he sits. Capuchins bring
    // small pieces to the mouth one-handed and hold whole food in both hands.
    eatOneHanded: {
      restClip: "Anim_Monkey_Sitting",
      bones:
        "^monkey_l_(Scapula|Humerus|HumerusRoll|Forearm|ForearmRoll\\d|Hand|Thumb_\\d|Index_\\d|Middle_\\d|Ring_\\d|Pinky_\\d)_bone$"
    },
    // A banana is held upright in both hands with its tip at the mouth: the teeth bite
    // the tip open and pull the peel down, then bite the flesh from the top. The jaw
    // opens about twice as far for a bite as it smacks in the grin.
    eatBanana: {
      mouth: ["monkey_l_Lip_hlp", "monkey_r_Lip_hlp"],
      knuckles: ["monkey_r_Index_1_bone", "monkey_r_Pinky_1_bone"],
      jaws: [
        { bone: "monkey_Mandible_bone", biteDegrees: 18, chewDegrees: 7 },
        { bone: "monkey_Jaw_bone", biteDegrees: 8, chewDegrees: 3 }
      ]
    }
  },
  palms: {
    left: ["monkey_l_Hand_bone", "monkey_l_Middle_1_bone"],
    right: ["monkey_r_Hand_bone", "monkey_r_Middle_1_bone"]
  },
  actions: {
    idle: { clip: "Anim_Monkey_Idle", label: "Rustig rondkijken", inPlace: true },
    climb: {
      clip: "Anim_Monkey_Climb",
      label: "Rondklimmen langs de kaarten",
      inPlace: true,
      contactAdapter: "wdallgraphics",
      prepareClip: "Anim_Monkey_IdleToSit",
      perchedClip: "Anim_Monkey_Sitting",
      exitClip: "Anim_Monkey_SittingToIdle",
      // The forward leap starts and ends in the Idle stance (< 0.06 degrees per bone).
      jumpClip: "Anim_Monkey_Jump",
      // Root motion of its counterpart Anim_Monkey_Jump.fbx (30 fps): the pelvis drops
      // until 0.4 s, pushes 0.341 m forward until take-off at 0.633 s, is highest at
      // 0.867 s on a 0.61 m arc after flying 2.533 m, touches down at 1.267 s and
      // slides 0.244 m until 1.4 s.
      leapMotion: {
        durationSeconds: 47 / 30,
        pushStartSeconds: 12 / 30,
        takeoffSeconds: 19 / 30,
        apexSeconds: 26 / 30,
        touchdownSeconds: 38 / 30,
        stopSeconds: 42 / 30,
        flightMeters: 2.533,
        apexMeters: 0.61
      },
      // The source chain for a wall: Sitting -> SittingToClimb -> Climb cycles ->
      // ClimbToSit -> Sitting. The mount ends in exactly the top-out's first pose
      // (< 0.04 degrees per bone), so a low wall needs no cycle in between.
      mountClip: "Anim_Monkey_SittingToClimb",
      climbClip: "Anim_Monkey_Climb",
      topOutClip: "Anim_Monkey_ClimbToSit",
      // True source grip points after the centered idle-origin correction.
      handXMeters: [0.1293, -0.128],
      gripZMeters: 0.24,
      perchedHeightMeters: 0.448,
      // The root-motion counterpart of Anim_Monkey_Climb rises 0.5 m per cycle.
      climbStrideMeters: 0.5,
      // A card face in front of him is climbed; he only leaps over a gap or down.
      // Standing on all fours takes about a metre of top, so a leap to the next
      // column of cards spans about that much as well.
      maxJumpRiseMeters: 1.3,
      maxJumpDropMeters: 2.6,
      maxJumpGapMeters: 1.1,
      wallReachMeters: 0.15,
      // Starts in exactly the Run pose and ends in it (< 0.04 degrees per bone). Its
      // counterpart runs one stride, takes off at 0.433 s, is highest at 0.667 s on a
      // 0.333 m arc after 1.225 m through the air, and touches down at 0.933 s.
      runLeap: {
        clip: "Anim_Monkey_JumpRun",
        motion: {
          takeoffSeconds: 13 / 30,
          apexSeconds: 20 / 30,
          touchdownSeconds: 28 / 30,
          apexMeters: 0.333,
          flightMeters: 1.225
        }
      },
      // Root-motion counterparts: ClimbDown loses 0.484 m per cycle, ClimbLeft and
      // ClimbRight move 0.4 m sideways per cycle along the wall.
      wallMoves: {
        descendClip: "Anim_Monkey_ClimbDown",
        descendStrideMeters: 0.484,
        shimmyLeftClip: "Anim_Monkey_ClimbLeft",
        shimmyRightClip: "Anim_Monkey_ClimbRight",
        shimmyStrideMeters: 0.4
      }
    },
    walk: {
      clip: "Anim_Monkey_Walk",
      label: "Een rondje lopen",
      inPlace: true,
      // Source root-motion counterpart: 0.5 m in 24 frames at 30 fps. Its last keyframe
      // repeats the one before it; the looped cycle leaves it out.
      speedMetersPerSecond: 0.625
    },
    run: {
      clip: "Anim_Monkey_Run",
      label: "Even rennen",
      inPlace: true,
      // Source root-motion counterpart: 1 m in 14 frames at 30 fps. Its last keyframe
      // repeats the one before it; the looped cycle leaves it out.
      speedMetersPerSecond: 30 / 14
    },
    jump: {
      clip: "Anim_Monkey_JumpInplace",
      label: "Een sprongetje maken",
      inPlace: true,
      posture: "sitting",
      enterClip: "Anim_Monkey_IdleToSit",
      exitClip: "Anim_Monkey_SittingToIdle"
    },
    eat: {
      clip: "Anim_Monkey_Eat",
      label: "Op zijn gemak eten",
      inPlace: true,
      posture: "sitting",
      enterClip: "Anim_Monkey_IdleToSit",
      exitClip: "Anim_Monkey_SittingToIdle"
    },
    sit: {
      clip: "Anim_Monkey_Sitting",
      label: "Lekker zitten",
      inPlace: true,
      posture: "sitting",
      persistent: true,
      enterClip: "Anim_Monkey_IdleToSit",
      exitClip: "Anim_Monkey_SittingToIdle"
    },
    // Smile and Ungry start and end in exactly the Sitting pose (< 0.05 degrees).
    smile: {
      clip: "Anim_Monkey_Smile",
      label: "Naar je grijnzen",
      inPlace: true,
      posture: "sitting",
      enterClip: "Anim_Monkey_IdleToSit",
      exitClip: "Anim_Monkey_SittingToIdle"
    },
    sulk: {
      clip: "Anim_Monkey_Ungry",
      label: "Even boos kijken",
      inPlace: true,
      posture: "sitting",
      enterClip: "Anim_Monkey_IdleToSit",
      exitClip: "Anim_Monkey_SittingToIdle"
    },
    // Hit starts and ends in exactly the Idle pose; its counterpart flinches back 13 cm
    // and returns to where it started.
    flinch: {
      clip: "Anim_Monkey_Hit",
      label: "Even schrikken",
      inPlace: true
    }
  }
}
